import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";

const BG_IMG =
  "https://images.unsplash.com/photo-1618720646037-fafa9f92fdc7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHx0b3VyJTIwZ3VpZGUlMjBzdW5zZXR8ZW58MHx8fHwxNzg3MjA2MjIwfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(t("toast_welcome_back"));
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FFFDF9] relative">
      <div className="absolute top-4 right-4 z-30 md:right-6 md:top-6">
        <LanguageSwitcher variant="glass" />
      </div>
      <div className="hidden md:block relative">
        <img src={BG_IMG} alt="Tour" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540]/70 via-[#0A2540]/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-display font-bold text-4xl leading-tight">{t("auth_side_title")}</div>
          <div className="mt-2 opacity-80">{t("auth_side_sub")}</div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="inline-flex items-center gap-2 mb-10" data-testid="brand-link">
            <div className="w-9 h-9 rounded-lg bg-[#0A2540] grid place-items-center text-white font-bold font-display">R</div>
            <span className="font-display font-semibold text-lg">ReviewFlow</span>
          </Link>
          <h1 className="font-display text-3xl font-bold text-[#0A2540]">{t("auth_login_title")}</h1>
          <p className="mt-2 text-slate-600">{t("auth_login_sub")}</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm">{t("field_email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 h-11"
                placeholder="you@business.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm">{t("field_password")}</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-[#E57C4F] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forget Passwords
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 h-11"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-md bg-[#0A2540] hover:bg-[#0A2540]/90 text-white text-base font-semibold"
            >
              {loading ? t("login_submitting") : t("login_submit")}
            </Button>
          </div>

          <p className="mt-8 text-sm text-slate-600">
            {t("no_account")}{" "}
            <Link to="/register" className="font-semibold text-[#E57C4F] hover:underline" data-testid="link-to-register">
              {t("signup_link")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
